"use client";

import * as React from "react";
import { setBearerToken, setIdentity } from "@/lib/api/http";
import {
  beginLogin, isOidcConfigured, logoutUrl, readClaims, refreshTokens,
  type Claims, type TokenSet,
} from "./oidc";

/**
 * Who is signed in, and the only place the access token exists.
 *
 * IN MEMORY. Not `localStorage`, not `sessionStorage`, not a script-readable
 * cookie. The console renders scan evidence, which is hostile markup by
 * construction (web/README, SDD §10.4), so a token in web storage is one
 * successful injection away from being exfiltrated — and it would be a token
 * scoped to a tenant's entire security programme. The cost is that a reload
 * has no token; the answer is a silent re-authentication against Keycloak's
 * own session, not a copy on disk.
 */

export type AuthStatus = "unconfigured" | "restoring" | "authenticated" | "anonymous";

interface AuthContextValue {
  status: AuthStatus;
  claims?: Claims;
  /** The organisation every request acts in (ADR-048's `org_id`). */
  organisationId?: string;
  /** `replaceHistory`: Keycloak's page takes this page's place in history (3.140). */
  signIn: (returnTo?: string, options?: { replaceHistory?: boolean }) => void;
  signOut: () => void;
  /** Called by the callback route once it has exchanged the code. */
  adopt: (tokens: TokenSet) => void;
  /** Called by the callback route when there was no session to restore. */
  giveUp: () => void;
}

const Ctx = React.createContext<AuthContextValue | undefined>(undefined);

/** Refresh this far ahead of expiry, so an in-flight request never carries a dead token. */
const REFRESH_MARGIN_MS = 30_000;

/**
 * Paths that must never trigger a silent restore.
 *
 * `/callback` is mid-flight and would restart the flow it is completing.
 * `/login` and the marketing page are the places an anonymous visitor is
 * entitled to be, and bouncing them to Keycloak on arrival would make the
 * sign-in page impossible to look at.
 */
const NO_RESTORE = new Set(["/callback", "/login", "/reset-password", "/"]);

function shouldAttemptRestore(pathname: string): boolean {
  return !NO_RESTORE.has(pathname) && !pathname.startsWith("/accept-invite");
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const configured = isOidcConfigured();
  const [status, setStatus] = React.useState<AuthStatus>(() => {
    if (!configured) return "unconfigured";
    return shouldAttemptRestore(window.location.pathname) ? "restoring" : "anonymous";
  });
  const [claims, setClaims] = React.useState<Claims | undefined>();

  // A ref, not state: the token must not be a render input, so no component
  // can put it in the DOM or leak it through a dependency array.
  const tokens = React.useRef<TokenSet | undefined>(undefined);
  const timer = React.useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const apply = React.useCallback((next: TokenSet | undefined) => {
    tokens.current = next;
    setBearerToken(next?.accessToken);
    const nextClaims = next ? readClaims(next.accessToken) : undefined;
    setIdentity(
      nextClaims && {
        sub: nextClaims.sub,
        organisationId: nextClaims.org_id,
        name: nextClaims.name ?? nextClaims.preferred_username,
        email: nextClaims.email,
      },
    );
    setClaims(nextClaims);
    setStatus(next ? "authenticated" : "anonymous");
  }, []);

  const scheduleRefresh = React.useCallback(
    function schedule(current: TokenSet) {
      if (timer.current) clearTimeout(timer.current);
      const delay = Math.max(1_000, current.expiresAt - Date.now() - REFRESH_MARGIN_MS);
      timer.current = setTimeout(async () => {
        if (!current.refreshToken) return apply(undefined);
        const next = await refreshTokens(current.refreshToken).catch(() => undefined);
        // A refresh that fails is a session that ended. Dropping to anonymous
        // is the honest result; retrying would spin against a dead session.
        if (!next) return apply(undefined);
        apply(next);
        schedule(next);
      }, delay);
    },
    [apply],
  );

  const adopt = React.useCallback(
    (next: TokenSet) => { apply(next); scheduleRefresh(next); },
    [apply, scheduleRefresh],
  );

  const giveUp = React.useCallback(() => apply(undefined), [apply]);

  React.useEffect(() => {
    if (!configured || !shouldAttemptRestore(window.location.pathname)) return;
    // `prompt=none` answers from Keycloak's session if there is one and returns
    // `login_required` if there is not — neither shows a form.
    void beginLogin(window.location.pathname + window.location.search, "none").catch(() => {
      setStatus("anonymous");
    });
    // Once. Re-entering would bounce the browser in a loop. (`configured` is
    // the only dependency: beginLogin is a module function, setStatus stable.)
  }, [configured]);

  React.useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);

  const value = React.useMemo<AuthContextValue>(
    () => ({
      status,
      claims,
      organisationId: claims?.org_id,
      adopt,
      giveUp,
      signIn: (returnTo, options) =>
        void beginLogin(returnTo ?? window.location.pathname, undefined, options?.replaceHistory ? "replace" : "push"),
      signOut: () => {
        if (timer.current) clearTimeout(timer.current);
        const idToken = tokens.current?.idToken;
        apply(undefined);
        // Ending the tab's session is not signing out — Keycloak's session
        // would sign the next visitor straight back in.
        window.location.assign(logoutUrl(idToken));
      },
    }),
    [status, claims, adopt, giveUp, apply],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = React.useContext(Ctx);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}

/**
 * The auth context if there is one. For the tenancy provider, which the tests
 * mount without an identity provider around it: absent means "no token will
 * ever arrive", which is the mock source's situation, not an error.
 */
export function useOptionalAuth(): AuthContextValue | undefined {
  return React.useContext(Ctx);
}
